import { Badge, Box, Button, Divider, IconButton, Tooltip } from "@mui/material";
import { ColumnsPanelTrigger, ExportCsv } from "@mui/x-data-grid";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { palette } from "../theme";
import { toolIconSx } from "./toolbarStyles";

// Mattoncini comuni ai toolbar delle griglie (Security, Antlia, Inventory), nello
// stile icon-button della pagina Aggregations.

// Box e non Toolbar: dataGridStyle applica bordi/padding a tutti i bottoni
// dentro .MuiToolbar-root, che romperebbero lo stile icon-button.
export const GridToolbarBar = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 1.5,
      px: 1.5,
      py: 1,
      backgroundColor: "#292929",
      borderBottom: "1px solid #1e1e1e",
    }}
  >
    {children}
  </Box>
);

export const ToolbarGroup = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>{children}</Box>
);

export const ToolbarDivider = () => (
  <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.12)" }} />
);

export const ToolbarSpacer = () => <Box sx={{ flex: 1 }} />;

export const ToolIconButton = ({ title, active = false, count = 0, dot = false, disabled = false, children, ...props }) => (
  <Tooltip arrow title={title}>
    {/* span: un bottone disabled non emette eventi, il Tooltip ha bisogno di un wrapper */}
    <span style={{ display: "inline-flex" }}>
      <IconButton
        size="small"
        disabled={disabled}
        sx={{ ...toolIconSx(active), "&.Mui-disabled": { color: "rgba(234,234,234,0.3)", borderColor: "rgba(255,255,255,0.08)" } }}
        aria-label={typeof title === "string" ? title : undefined}
        {...props}
      >
        <Badge
          color="primary"
          variant={dot ? "dot" : "standard"}
          badgeContent={dot ? undefined : count}
          invisible={dot ? !active : !count}
          sx={{ "& .MuiBadge-badge": dot ? {} : { fontSize: 10, height: 16, minWidth: 16, top: -4, right: -4 } }}
        >
          {children}
        </Badge>
      </IconButton>
    </span>
  </Tooltip>
);

export const ColumnsButton = () => (
  <Tooltip arrow title="Columns">
    <ColumnsPanelTrigger render={<IconButton size="small" sx={toolIconSx()} aria-label="Columns" />}>
      <ViewColumnOutlinedIcon fontSize="small" />
    </ColumnsPanelTrigger>
  </Tooltip>
);

export const FilterButton = ({ count = 0, onClick }) => (
  <ToolIconButton title={count ? `Filters (${count} active)` : "Filters"} active={count > 0} count={count} onClick={onClick}>
    <FilterListIcon fontSize="small" />
  </ToolIconButton>
);

// Azione con etichetta (Export, Import...): resta leggibile anche fuori dal gruppo filtri.
export const ToolTextButton = ({ icon, children, sx, ...props }) => (
  <Button
    size="small"
    startIcon={icon}
    sx={{
      color: "#eaeaea",
      border: "1px solid rgba(255,255,255,0.14)",
      borderRadius: "8px",
      px: 1.5,
      height: 34,
      "&:hover": { borderColor: palette.primary.light, backgroundColor: "rgba(69,144,149,0.12)" },
      ...sx,
    }}
    {...props}
  >
    {children}
  </Button>
);

export const ExportCsvButton = ({ options }) => (
  <ExportCsv options={options} render={<ToolTextButton icon={<SaveAltIcon fontSize="small" />} />}>
    Export
  </ExportCsv>
);

// Toggle a icona per mostrare/nascondere una categoria di righe (Backup, ACK'd...).
// Colorato = categoria visibile, barra diagonale = nascosta.
export const VisibilityToggle = ({ label, icon: Icon, visible, onToggle, description, accent = palette.primary.main }) => (
  <Tooltip
    arrow
    title={
      <Box sx={{ py: 0.25 }}>
        <Box sx={{ fontWeight: 600 }}>{label}: {visible ? "shown" : "hidden"}</Box>
        {description && <Box sx={{ opacity: 0.8 }}>{description}</Box>}
        <Box sx={{ opacity: 0.6, mt: 0.5 }}>Click to {visible ? "hide" : "show"}</Box>
      </Box>
    }
  >
    <IconButton
      size="small"
      onClick={onToggle}
      aria-pressed={visible}
      aria-label={`${visible ? "Hide" : "Show"} ${label}`}
      sx={{
        ...toolIconSx(),
        position: "relative",
        color: visible ? accent : "rgba(234,234,234,0.4)",
        borderColor: visible ? accent : "rgba(255,255,255,0.14)",
        backgroundColor: visible ? `${accent}1f` : "transparent",
        "&:hover": { borderColor: accent, backgroundColor: `${accent}1f` },
        "&::after": visible ? {} : {
          content: '""',
          position: "absolute",
          width: 22,
          height: 1.5,
          backgroundColor: "rgba(234,234,234,0.6)",
          transform: "rotate(-45deg)",
          borderRadius: 1,
        },
      }}
    >
      <Icon fontSize="small" />
    </IconButton>
  </Tooltip>
);

// Toolbar minimale (Columns + Export) per le tabelle senza filtri dedicati.
export const StandardGridToolbar = () => (
  <GridToolbarBar>
    <ToolbarGroup>
      <ColumnsButton />
      <ExportCsvButton />
    </ToolbarGroup>
  </GridToolbarBar>
);
